import React, { useState } from "react";
import { TFunction } from "i18next";
import { RNMaterialnoIssueRow, RNWorkOrderDoc, useRNWorkOrder } from "./useRNWorkOrder";
import { RazmerRow } from "../components/OSRazmerTable";
import { MatGroup } from "../components/OSMaterialnoTable";
import { SubprocessItemRow } from "../components/OSSubprocessItemsTable";
import { RNMaterialPriemRow } from "../components/RNMaterialPriemTable";
import { SubprocessOption } from "../select/SubprocessCells";
import { parseDecimalInput, formatQtyForDisplay } from "../utils/decimalInput";
import { formatRNDocumentError } from "../utils/rnDocumentError";
import { DocumentType, WorkOrderRole } from "../types/Types";
import { isFinishedProduct, isIntermediateOrSemi } from "../utils/accountingTypeGroup";
import { issueEntryRole, receiptEntryRole, currentDocTypeCode } from "../utils/workOrderRole";
import { isRowVerified } from "../utils/rnRowVerification";

interface UseRNMaterialDocumentActionsArgs {
  rnWorkOrder: RNWorkOrderDoc | null;
  rnDocumentCode: string;
  activeStage: string;
  isRazmerStage: boolean;
  isSaving: boolean;
  isDirty: boolean;
  // True when a batch/expiry suggestion is unsaved (screen-level, see RNWorkOrder.tsx).
  // Only the RECEIPT action (7201) below must wait for it — issuance (6421/6423) doesn't
  // read those saved values, so it stays gated on real isDirty only.
  hasUnsavedSuggestions: boolean;
  pendingDocument: WorkOrderRole | 'transfer' | null;
  materialnoGroups: MatGroup[];
  razmerRows: RazmerRow[];
  subprocesses: SubprocessOption[];
  subprocessItemRows: SubprocessItemRow[];
  documentTypes: DocumentType[];
  issueMaterial6421: ReturnType<typeof useRNWorkOrder>["issueMaterial6421"];
  issueRastur6423: ReturnType<typeof useRNWorkOrder>["issueRastur6423"];
  receiveMaterial7201: ReturnType<typeof useRNWorkOrder>["receiveMaterial7201"];
  showToast: (message: string, type: "success" | "error") => void;
  t: TFunction;
}

// Издај материјал (6421) / Креирај растур (6423) / Материјално прием (7201). Move-only
// out of RNWorkOrder.tsx — same state, same memo/callback deps, same behavior.
export const useRNMaterialDocumentActions = ({
  rnWorkOrder,
  rnDocumentCode,
  activeStage,
  isRazmerStage,
  isSaving,
  isDirty,
  hasUnsavedSuggestions,
  pendingDocument,
  materialnoGroups,
  razmerRows,
  subprocesses,
  subprocessItemRows,
  documentTypes,
  issueMaterial6421,
  issueRastur6423,
  receiveMaterial7201,
  showToast,
  t,
}: UseRNMaterialDocumentActionsArgs) => {
  // Stock document generation is tracked separately from saving the RN actuals.
  const [isIssuingMaterial6421, setIsIssuingMaterial6421] = useState(false);
  // Прими материјал (7201) — same isolation as isIssuingMaterial6421, opposite stock
  // direction.
  const [isReceivingMaterial7201, setIsReceivingMaterial7201] = useState(false);
  // Креирај растур (6423) — same isolation as isIssuingMaterial6421, separate stock
  // document (rastur, not raw-material issuance).
  const [isIssuingRastur6423, setIsIssuingRastur6423] = useState(false);
  // Stage picker for Креирај растур — Размерување has no single "active stage", so the
  // operator chooses which subprocess's rastur rows to issue from this modal.
  const [rasturModalOpen, setRasturModalOpen] = useState(false);

  // Издај материјал (6421) — rows eligible to be issued from stock for the active stage:
  // Материјално rows belonging to the active subprocess with a non-empty Издадена
  // количина AND ✓ potvrdeno. osRowId doubles as the row's rowKey, same convention as
  // razmer/materialno actual rows.
  const materialno6421EligibleRows = React.useMemo(() => {
    if (!activeStage || isRazmerStage) return [];
    const rows: { rowKey: string; item: string; ident: string; name: string; unitCode: string }[] = [];
    // Semi-finished rows (osRowId = interProducts rowId) are issued by Издади МП, not here.
    const interProductRowIds = new Set(subprocessItemRows.map((r) => r.rowId).filter(Boolean));
    materialnoGroups
      .filter((g) => g.subprocessId === activeStage)
      .forEach((g) => {
        g.items.forEach((it) => {
          // The server builds 6421 rows from materialno only and rejects unknown rowKeys
          // (400 invalidIssueRow), so intermediates must go through Издади МП instead.
          if (!it.osRowId || !it.itemId) return;
          if (interProductRowIds.has(it.osRowId)) return;
          if (!(it.measuredQty || "").trim()) return;
          if (!it.confirmed) return;
          if (!isRowVerified(it)) return;
          rows.push({ rowKey: it.osRowId, item: it.itemId, ident: it.ident, name: it.name, unitCode: it.unitCode });
        });
      });
    return rows;
  }, [activeStage, isRazmerStage, materialnoGroups, subprocessItemRows]);

  // Confirmed rows that still wait for a second person's Проверил — the server rejects them.
  const materialno6421HasUnverified = React.useMemo(() => {
    if (!activeStage || isRazmerStage) return false;
    const interProductRowIds = new Set(subprocessItemRows.map((r) => r.rowId).filter(Boolean));
    return materialnoGroups
      .filter((g) => g.subprocessId === activeStage)
      .some((g) => g.items.some((it) => (
        !!it.osRowId
        && !!it.itemId
        && !interProductRowIds.has(it.osRowId)
        && !!(it.measuredQty || "").trim()
        && !!it.confirmed
        && !isRowVerified(it)
      )));
  }, [activeStage, isRazmerStage, materialnoGroups, subprocessItemRows]);

  // The active stage's own entry in rn.materialnoIssues, if this stage was already issued.
  const activeStageMaterialnoIssue = React.useMemo(() => {
    if (!activeStage) return undefined;
    return (rnWorkOrder?.materialnoIssues || []).find((issue) => issue.subprocessId === activeStage && issueEntryRole(issue, documentTypes) === 'issue-materials');
  }, [activeStage, rnWorkOrder?.materialnoIssues, documentTypes]);

  const materialno6421Disabled = !!(
    isSaving
    || pendingDocument !== null
    || isReceivingMaterial7201
    || isIssuingMaterial6421
    || isDirty
    || !rnWorkOrder?._id
    || materialno6421EligibleRows.length === 0
    || materialno6421HasUnverified
    || activeStageMaterialnoIssue
  );

  const materialno6421Title = activeStageMaterialnoIssue
    ? t('os.materialno.issue6421AlreadyIssued', { number: activeStageMaterialnoIssue.documentNumber || '' })
    : !rnWorkOrder?._id
      ? t('os.materialno.issue6421Unsaved')
      : isDirty
        ? t('os.materialno.issue6421Dirty')
        : isSaving || isIssuingMaterial6421 || isReceivingMaterial7201 || pendingDocument !== null
          ? t('os.materialno.issue6421Saving')
          : materialno6421HasUnverified
            ? t('os.materialno.issue6421NotVerified')
            : materialno6421EligibleRows.length === 0
              ? t('os.materialno.issue6421NoRows')
              : t('os.materialno.issue6421Tooltip');

  const handleIssueMaterial6421 = React.useCallback(async () => {
    if (!rnWorkOrder?._id || !activeStage || materialno6421EligibleRows.length === 0) return;
    try {
      setIsIssuingMaterial6421(true);
      const result = await issueMaterial6421(rnWorkOrder._id, activeStage, materialno6421EligibleRows);
      showToast(
        t('os.materialno.issue6421Success', { number: result.document?.number || '' }),
        'success'
      );
    } catch (err: any) {
      const data = err?.response?.data;
      showToast(formatRNDocumentError(data, t, 'os.materialno.issue6421Failed'), 'error');
    } finally {
      setIsIssuingMaterial6421(false);
    }
  }, [activeStage, issueMaterial6421, materialno6421EligibleRows, rnWorkOrder?._id, showToast, t]);

  // Креирај растур (6423) — Размерување shows ALL subprocesses' rows in one combined
  // table (activeStage is pinned to RAZMER_STAGE_ID "__razmer__" there, which never
  // equals a row's real subprocessId), so there is no single "active stage" to derive
  // the required subprocessId from. Instead: group eligible rows by their OWN
  // subprocessId and let the operator pick which stage to issue via RNRasturStageModal.
  // A row is eligible with osRowId + itemId + a positive numeric Растур (rastur) value —
  // same convention as materialno6421EligibleRows. Rows without osRowId (ephemeral keys —
  // e.g. un-migrated normativs whose razmer[] subdocs have no _id) can never be issued.
  const rastur6423StageOptions = React.useMemo(() => {
    const bySubprocess = new Map<string, RNMaterialnoIssueRow[]>();
    razmerRows.forEach((row) => {
      if (!row.subprocessId || !row.osRowId || !row.itemId) return;
      const rastur = parseDecimalInput(row.rastur);
      if (rastur === null || rastur <= 0) return;
      const rows = bySubprocess.get(row.subprocessId) || [];
      rows.push({ rowKey: row.osRowId, item: row.itemId, ident: row.ident, name: row.name, unitCode: row.unitCode });
      bySubprocess.set(row.subprocessId, rows);
    });
    const nameById = new Map(subprocesses.map((sp) => [sp._id, sp.name]));
    return Array.from(bySubprocess.entries())
      .map(([subprocessId, rows]) => {
        const existing = (rnWorkOrder?.materialnoIssues || []).find(
          (issue) => issue.subprocessId === subprocessId && issueEntryRole(issue, documentTypes) === 'waste'
        );
        return {
          subprocessId,
          name: nameById.get(subprocessId) || subprocessId,
          rows,
          existingDocumentNumber: existing?.documentNumber,
        };
      })
      .sort((a, b) => a.name.localeCompare(b.name, 'mk'));
  }, [razmerRows, subprocesses, rnWorkOrder?.materialnoIssues, documentTypes]);

  const rastur6423Disabled = !!(
    isSaving
    || pendingDocument !== null
    || isReceivingMaterial7201
    || isIssuingMaterial6421
    || isIssuingRastur6423
    || isDirty
    || !rnWorkOrder?._id
    || rastur6423StageOptions.length === 0
  );

  const rastur6423Title = !rnWorkOrder?._id
    ? t('os.razmer.rastur6423Unsaved')
    : isDirty
      ? t('os.razmer.rastur6423Dirty')
      : isSaving || isIssuingRastur6423 || isIssuingMaterial6421 || isReceivingMaterial7201 || pendingDocument !== null
        ? t('os.razmer.rastur6423Saving')
        : rastur6423StageOptions.length === 0
          ? t('os.razmer.rastur6423NoRows')
          : t('os.razmer.rastur6423Tooltip');

  const handleSelectRasturStage = React.useCallback(async (subprocessId: string) => {
    if (!rnWorkOrder?._id) return;
    const stage = rastur6423StageOptions.find((s) => s.subprocessId === subprocessId);
    if (!stage || stage.existingDocumentNumber || stage.rows.length === 0) return;
    try {
      setIsIssuingRastur6423(true);
      const result = await issueRastur6423(rnWorkOrder._id, subprocessId, stage.rows);
      showToast(
        t('os.razmer.rastur6423Success', { number: result.document?.number || '' }),
        'success'
      );
      setRasturModalOpen(false);
    } catch (err: any) {
      const data = err?.response?.data;
      showToast(formatRNDocumentError(data, t, 'os.razmer.rastur6423Failed'), 'error');
    } finally {
      setIsIssuingRastur6423(false);
    }
  }, [issueRastur6423, rastur6423StageOptions, rnWorkOrder?._id, showToast, t]);

  // ── Материјално прием (7201) ────────────────────────────────────────────────────────
  // interProducts rows PRODUCED by the active stage (subprocessId === activeStage — the
  // PRODUCING subprocess, never issueSubprocessId, per §1 of the spec: `subprocess` is
  // the producer, `issueSubprocess` is the consumer — the IngredientsSchema comment near
  // that field is worded backwards).
  const subprocessNameById = React.useMemo(
    () => new Map(subprocesses.map((sp) => [sp._id, sp.name || ""])),
    [subprocesses]
  );

  const activeStageIssuedQtyTotal = React.useMemo(() => {
    const total = (rnWorkOrder?.materialnoIssues || [])
      .filter((entry) => entry.subprocessId === activeStage)
      .flatMap((entry) => entry.rows)
      .reduce((sum, row) => sum + (row.quantity || 0), 0);
    return total > 0 ? formatQtyForDisplay(total) : '';
  }, [activeStage, rnWorkOrder?.materialnoIssues]);

  const activeStageInterProductReceipt = React.useMemo(() => {
    if (!activeStage) return undefined;
    return (rnWorkOrder?.interProductReceipts || []).find((receipt) => receipt.subprocessId === activeStage && receiptEntryRole(receipt, documentTypes) === 'receive-intermediate');
  }, [activeStage, rnWorkOrder?.interProductReceipts, documentTypes]);

  const visibleInterProductRows = React.useMemo((): RNMaterialPriemRow[] => {
    if (!activeStage || isRazmerStage) return [];
    const workOrderType = documentTypes.find((type) => type.code === rnDocumentCode);
    return subprocessItemRows
      .filter((r) => r.subprocessId === activeStage && r.itemId)
      .map((r) => {
        const isFinished = isFinishedProduct({ group: r.accountingTypeGroup });
        const wantedRole = isFinished ? 'receive-finished' : 'receive-intermediate';
        const receptionRule = workOrderType?.receptionCreations?.find((rule) => {
          const accountingType = rule.accountingType as unknown as { code?: string } | string;
          return typeof accountingType === 'object' && accountingType?.code === r.accountingTypeCode;
        });
        const materialDocument = receptionRule?.materialDocument as unknown as { code?: string } | string | undefined;
        const ruleDocumentCode = typeof materialDocument === 'object'
          ? materialDocument?.code
          : documentTypes.find((type) => type._id === materialDocument)?.code;
        const receipt = (rnWorkOrder?.interProductReceipts || []).find((entry) =>
          entry.subprocessId === activeStage
          && receiptEntryRole(entry, documentTypes) === wantedRole
          && entry.rows.some((receiptRow) => receiptRow.rowKey === r.rowId)
        );
        return {
          ...r,
          subprocessName: subprocessNameById.get(r.subprocessId) || '',
          issuedQty: isFinished ? '' : activeStageIssuedQtyTotal,
          documentTypeCode: (receipt && currentDocTypeCode(receipt, documentTypes)) || ruleDocumentCode || '',
          documentNumber: receipt?.documentNumber || '',
        };
      });
  }, [activeStage, isRazmerStage, subprocessItemRows, subprocessNameById, activeStageIssuedQtyTotal, rnWorkOrder?.interProductReceipts, documentTypes, rnDocumentCode]);

  // Eligible for "Прими": row with a rowId (interProducts subdoc _id) + item, a typed
  // Примена количина, and the explicit ✓ Потврдено checkbox — mirrors
  // materialno6421EligibleRows's shape/gate, keyed to rowId instead of osRowId.
  const interProduct7201EligibleRows = React.useMemo(() => {
    const rows: { rowKey: string; item: string; ident: string; name: string; unitCode: string }[] = [];
    visibleInterProductRows.forEach((r) => {
      if (!r.rowId || !r.itemId || !isIntermediateOrSemi({ group: r.accountingTypeGroup })) return;
      if (!(r.measuredQty || '').trim()) return;
      if (!r.confirmed) return;
      if (!isRowVerified(r)) return;
      rows.push({ rowKey: r.rowId, item: r.itemId, ident: r.ident, name: r.name, unitCode: r.unitCode });
    });
    return rows;
  }, [visibleInterProductRows]);

  const interProduct7201HasUnverified = React.useMemo(() => visibleInterProductRows.some((r) => (
    !!r.rowId
    && !!r.itemId
    && isIntermediateOrSemi({ group: r.accountingTypeGroup })
    && !!(r.measuredQty || '').trim()
    && !!r.confirmed
    && !isRowVerified(r)
  )), [visibleInterProductRows]);

  // The 7201 receipt route reads the saved batch/expiry suggestion — block it on an
  // unsaved suggestion too, not just a real edit (see hasUnsavedSuggestions doc above).
  const interProduct7201Blocked = isDirty || hasUnsavedSuggestions;

  const interProduct7201Disabled = !!(
    isSaving
    || isReceivingMaterial7201
    || isIssuingMaterial6421
    || pendingDocument !== null
    || interProduct7201Blocked
    || !rnWorkOrder?._id
    || interProduct7201EligibleRows.length === 0
    || interProduct7201HasUnverified
    || activeStageInterProductReceipt
  );

  const interProduct7201Title = activeStageInterProductReceipt
    ? t('os.materialno.receive7201AlreadyReceived', { number: activeStageInterProductReceipt.documentNumber || '' })
    : !rnWorkOrder?._id
      ? t('os.materialno.receive7201Unsaved')
      : isDirty
        ? t('os.materialno.receive7201Dirty')
        : hasUnsavedSuggestions
          ? t('os.materialno.receive7201Suggestions')
        : isSaving || isReceivingMaterial7201 || isIssuingMaterial6421 || pendingDocument !== null
          ? t('os.materialno.receive7201Saving')
          : interProduct7201HasUnverified
          ? t('os.materialno.receive7201NotVerified')
          : interProduct7201EligibleRows.length === 0
            ? t('os.materialno.receive7201NoRows')
            : t('os.materialno.receive7201Tooltip');

  const handleReceiveMaterial7201 = React.useCallback(async () => {
    if (!rnWorkOrder?._id || !activeStage || interProduct7201EligibleRows.length === 0) return;
    try {
      setIsReceivingMaterial7201(true);
      const result = await receiveMaterial7201(rnWorkOrder._id, activeStage, interProduct7201EligibleRows);
      showToast(
        t('os.materialno.receive7201Success', { number: result.document?.number || '' }),
        'success'
      );
    } catch (err: any) {
      const data = err?.response?.data;
      showToast(formatRNDocumentError(data, t, 'os.materialno.receive7201Failed'), 'error');
    } finally {
      setIsReceivingMaterial7201(false);
    }
  }, [activeStage, receiveMaterial7201, interProduct7201EligibleRows, rnWorkOrder?._id, showToast, t]);

  const documentBusy = isIssuingMaterial6421 || isReceivingMaterial7201 || isIssuingRastur6423 || pendingDocument !== null;

  return {
    isIssuingMaterial6421,
    isReceivingMaterial7201,
    isIssuingRastur6423,
    rasturModalOpen,
    setRasturModalOpen,
    subprocessNameById,
    materialno6421Disabled,
    materialno6421Title,
    handleIssueMaterial6421,
    rastur6423StageOptions,
    rastur6423Disabled,
    rastur6423Title,
    handleSelectRasturStage,
    visibleInterProductRows,
    interProduct7201Disabled,
    interProduct7201Title,
    handleReceiveMaterial7201,
    documentBusy,
  };
};
